import { useState } from 'react';

type Plan = 'Premium' | 'Enterprise';
type Mode = 'Suggest' | 'Approve' | 'Automate';
type Status = 'Completed' | 'Awaiting approval' | 'Suggested' | 'At risk' | 'Sent';

type Agent = {
  icon: string;
  /** Tile colour, matching the agents console. */
  tone: string;
  title: string;
  summary: string;
  watches: string;
  does: string;
  plan: Plan;
  /** The level most teams start with; every agent can be set to any level. */
  mode: Mode;
  example: { time: string; text: string; status: Status };
};

/** Agent roles and sample activity. Example entries use the same sample data as the product screenshots. */
const AGENTS: Agent[] = [
  {
    icon: 'ri-share-line',
    tone: 'bg-blue-50 text-blue-600',
    title: 'Dispatch & allocation',
    summary: 'Assigns every order to the right driver.',
    watches: 'New orders, driver location, capacity, shift windows and SLA risk.',
    does: 'Assigns each order to the best available driver and keeps rebalancing as the day changes.',
    plan: 'Premium',
    mode: 'Automate',
    example: { time: '10:14 AM', text: 'Assigned ODR-78262 to Neha Bansal, based on proximity and skills.', status: 'Completed' },
  },
  {
    icon: 'ri-git-branch-line',
    tone: 'bg-emerald-50 text-emerald-600',
    title: 'Route recovery',
    summary: 'Replans routes that fall behind.',
    watches: 'Missed windows, traffic spikes and orders added mid-route.',
    does: 'Re-sequences or re-optimizes the affected routes and offers alternatives, without waiting for a planner.',
    plan: 'Enterprise',
    mode: 'Approve',
    example: { time: '10:05 AM', text: 'Generated 3 alternative plans for delayed routes in North Zone.', status: 'Suggested' },
  },
  {
    icon: 'ri-alarm-warning-line',
    tone: 'bg-red-50 text-red-600',
    title: 'Exception containment',
    summary: 'Resolves late drivers and failed attempts.',
    watches: 'Failed attempts, address issues, breakdowns and late drivers.',
    does: 'Proposes reassignments or customer updates, and escalates to a person with the full context.',
    plan: 'Enterprise',
    mode: 'Approve',
    example: { time: '10:15 AM', text: 'Proposed moving stops 12 and 14 to Amit Kumar, 1.2 km away with spare capacity.', status: 'Awaiting approval' },
  },
  {
    icon: 'ri-shield-star-line',
    tone: 'bg-violet-50 text-violet-600',
    title: 'SLA guardian',
    summary: 'Catches SLA risk before it becomes a breach.',
    watches: 'Promised ETAs and contractual cutoffs for every order.',
    does: 'Re-prioritizes work and alerts the team while there is still time to save the delivery.',
    plan: 'Enterprise',
    mode: 'Automate',
    example: { time: '10:18 AM', text: 'Flagged Stop 9 (ODR-78240) at risk, 7 min behind schedule.', status: 'At risk' },
  },
  {
    icon: 'ri-whatsapp-line',
    tone: 'bg-green-50 text-green-600',
    title: 'Customer comms',
    summary: 'Keeps customers updated automatically.',
    watches: 'ETA changes, delays and completed deliveries.',
    does: 'Sends proactive SMS, email or WhatsApp updates in your brand’s tone.',
    plan: 'Enterprise',
    mode: 'Automate',
    example: { time: '10:16 AM', text: 'Sent a new ETA to 2 customers via WhatsApp for stops 12 and 14.', status: 'Completed' },
  },
  {
    icon: 'ri-truck-line',
    tone: 'bg-orange-50 text-orange-600',
    title: 'Carrier matcher',
    summary: 'Picks the right carrier for each load.',
    watches: 'Lane, load, service level and carrier performance.',
    does: 'Recommends the best carrier or 3PL for each shipment and rebooks when one fails.',
    plan: 'Enterprise',
    mode: 'Suggest',
    example: { time: '10:12 AM', text: 'Suggested a partner carrier for HUB-2, with the best rate and ETA for the current load.', status: 'Suggested' },
  },
  {
    icon: 'ri-community-line',
    tone: 'bg-purple-50 text-purple-600',
    title: 'Hub load-balancing',
    summary: 'Stops one hub becoming the bottleneck.',
    watches: 'Inbound volume, sort windows and staffing at each hub.',
    does: 'Shifts orders between hubs before one runs over capacity.',
    plan: 'Enterprise',
    mode: 'Suggest',
    example: { time: '10:10 AM', text: 'Reallocated 12 orders from Hebbal to KR Puram due to high load.', status: 'Completed' },
  },
  {
    icon: 'ri-user-voice-line',
    tone: 'bg-sky-50 text-sky-600',
    title: 'Driver coach',
    summary: 'Gives drivers timely, specific guidance.',
    watches: 'Idle time, route adherence and safety patterns.',
    does: 'Sends each driver a specific nudge in the app instead of a generic broadcast.',
    plan: 'Enterprise',
    mode: 'Suggest',
    example: { time: '10:22 AM', text: 'Nudged Vikram Singh about 14 min idle time before Stop 6.', status: 'Sent' },
  },
];

const MODE_STYLE: Record<Mode, { chip: string; hint: string }> = {
  Suggest: { chip: 'bg-gray-100 text-gray-700', hint: 'Recommends; a person decides.' },
  Approve: { chip: 'bg-amber-50 text-amber-700', hint: 'Prepares the action; you confirm in one tap.' },
  Automate: { chip: 'bg-blue-50 text-blue-700', hint: 'Acts on its own within your guardrails.' },
};

const STATUS_STYLE: Record<Status, string> = {
  Completed: 'bg-emerald-50 text-emerald-700',
  'Awaiting approval': 'bg-amber-50 text-amber-700',
  Suggested: 'bg-blue-50 text-blue-700',
  'At risk': 'bg-orange-50 text-orange-700',
  Sent: 'bg-emerald-50 text-emerald-700',
};

function PlanBadge({ plan }: { plan: Plan }) {
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
        plan === 'Premium' ? 'bg-amber-100 text-amber-800' : 'bg-gray-900 text-white'
      }`}
    >
      {plan}
    </span>
  );
}

/** Interactive roster: pick an agent on the left, see what it watches, does and a sample action on the right. */
export default function AgentsExplorer() {
  const [active, setActive] = useState(0);
  const agent = AGENTS[active];
  const mode = MODE_STYLE[agent.mode];

  return (
    <section className="bg-white py-20 lg:py-28" aria-labelledby="agents-roster">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-12 max-w-2xl">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-700">The agents</p>
          <h2 id="agents-roster" className="mt-2 text-3xl font-bold tracking-tight text-gray-900 lg:text-4xl">
            Eight agents, each with one job
          </h2>
          <p className="mt-4 leading-relaxed text-gray-600">
            Pick an agent to see what it watches, what it does and what its work looks like in the command centre. Auto-dispatch comes
            with Premium; the full team is on Enterprise.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-8">
          {/* Roster */}
          <div role="tablist" aria-label="AI agents" aria-orientation="vertical" className="-mx-6 flex min-w-0 snap-x gap-2 overflow-x-auto px-6 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:col-span-5 lg:mx-0 lg:grid lg:grid-cols-1 lg:overflow-visible lg:px-0 lg:pb-0">
            {AGENTS.map((a, i) => {
              const selected = i === active;
              return (
                <button
                  key={a.title}
                  type="button"
                  role="tab"
                  id={`agent-tab-${i}`}
                  aria-selected={selected}
                  aria-controls="agent-panel"
                  onClick={() => setActive(i)}
                  className={`flex shrink-0 snap-start items-center gap-3 rounded-xl border px-3 py-2 text-start transition-all lg:w-full lg:px-3.5 lg:py-3 ${
                    selected ? 'border-blue-500 bg-blue-50/50 shadow-sm ring-1 ring-blue-500/20' : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-slate-50'
                  }`}
                >
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg lg:h-10 lg:w-10 lg:rounded-xl ${a.tone}`}>
                    <i className={`${a.icon} text-lg`} aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="whitespace-nowrap font-semibold text-gray-900 lg:truncate">{a.title}</span>
                      {a.plan === 'Premium' && <PlanBadge plan="Premium" />}
                    </span>
                    <span className="hidden truncate text-sm text-gray-500 lg:block">{a.summary}</span>
                  </span>
                  <i className={`ri-arrow-right-s-line hidden text-xl rtl:rotate-180 lg:block ${selected ? 'text-blue-600' : 'text-gray-300'}`} aria-hidden />
                </button>
              );
            })}
          </div>

          {/* Detail */}
          <div
            id="agent-panel"
            role="tabpanel"
            aria-labelledby={`agent-tab-${active}`}
            className="min-w-0 lg:col-span-7"
          >
            <div key={agent.title} className="flex h-full flex-col rounded-3xl border border-gray-200 bg-gradient-to-b from-slate-50 to-white p-6 opacity-0 animate-fade-in-up sm:p-8">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-center gap-4">
                  <span className={`flex h-14 w-14 items-center justify-center rounded-2xl ${agent.tone}`}>
                    <i className={`${agent.icon} text-2xl`} aria-hidden />
                  </span>
                  <div>
                    <h3 className="text-2xl font-bold tracking-tight text-gray-900">{agent.title}</h3>
                    <p className="mt-0.5 text-gray-600">{agent.summary}</p>
                  </div>
                </div>
                <PlanBadge plan={agent.plan} />
              </div>

              <dl className="mt-8 grid gap-4 sm:grid-cols-2">
                <div className="rounded-2xl border border-gray-200 bg-white p-5">
                  <dt className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-gray-400">
                    <i className="ri-eye-line text-sm" aria-hidden />
                    Watches
                  </dt>
                  <dd className="mt-2 text-sm leading-relaxed text-gray-700">{agent.watches}</dd>
                </div>
                <div className="rounded-2xl border border-gray-200 bg-white p-5">
                  <dt className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-gray-400">
                    <i className="ri-flashlight-line text-sm" aria-hidden />
                    Does
                  </dt>
                  <dd className="mt-2 text-sm leading-relaxed text-gray-700">{agent.does}</dd>
                </div>
              </dl>

              <div className="mt-4 rounded-2xl border border-gray-200 bg-white p-5">
                <p className="flex items-center justify-between gap-3 text-[11px] font-bold uppercase tracking-wider text-gray-400">
                  <span className="flex items-center gap-2">
                    <i className="ri-pulse-line text-sm" aria-hidden />
                    In the activity feed
                  </span>
                  <span className="font-medium normal-case tracking-normal text-gray-400">Sample data</span>
                </p>
                <div className="mt-3 flex items-start gap-3">
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${agent.tone}`}>
                    <i className={`${agent.icon} text-base`} aria-hidden />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-800">
                      <span className="me-2 text-xs tabular-nums text-gray-400">{agent.example.time}</span>
                      <span className="font-semibold">{agent.title}</span> · {agent.example.text}
                    </p>
                  </div>
                  <span className={`shrink-0 rounded-lg px-2.5 py-1 text-xs font-semibold ${STATUS_STYLE[agent.example.status]}`}>
                    {agent.example.status}
                  </span>
                </div>
              </div>

              <div className="mt-auto flex flex-wrap items-center justify-between gap-3 pt-6">
                <p className="flex items-center gap-2 text-sm text-gray-600">
                  <span className="text-gray-500">Typical starting mode:</span>
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${mode.chip}`}>{agent.mode}</span>
                  <span className="hidden text-gray-500 sm:inline">{mode.hint}</span>
                </p>
                <a href="#agents-control" className="inline-flex items-center gap-1 text-sm font-semibold text-blue-700 hover:text-blue-600">
                  Set autonomy
                  <i className="ri-arrow-down-line" aria-hidden />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
